/**
 * Display steps for different statuses/goals
 *
 * * Steps can be selected to record one selected step to data (optional props: checkedValue, onCheck)
 * * Steps can also be edited one at a time (optional props: editable, onEdit)
 */

import TextFieldArea from 'components/TextFieldArea';
import RadioButton from 'material-ui/RadioButton';
import { bool, func, object, string } from 'prop-types';
import React from 'react';
import { colors } from 'styles/provider';

import {
  StepsWrapper,
  Step,
  Radio,
  ColorBar,
  TextAreaContainer,
  TextContainer,
  EditTextArea,
  EditText,
} from './ControlSteps.less';

export const ControlStep = ({
  color,
  header,
  contentText,
  checked,
  onCheck,
  editable,
  onEdit,
  editing,
  setEditing,
  unsetEditing,
  cognition,
  fm,
}) => {
  const barColor =
    color === 'green'
      ? colors.clrHeatMap0
      : color === 'lightYellow'
        ? colors.clrHeatMap1
        : color === 'yellow'
          ? colors.clrHeatMap1
          : color === 'darkYellow'
            ? colors.clrHeatMap5
            : color === 'red'
              ? colors.clrHeatMap10
              : '#fff';

  const leftMarginFactor =
    color === 'green' || color === 'red' ? 2 : color === 'lightYellow' || color === 'darkYellow' ? 1 : 0;

  const checkable = checked === true || checked === false;

  return (
    <div
      className={Step}
      style={{ marginLeft: `${leftMarginFactor * 5}rem`, cursor: checkable ? 'pointer' : 'default' }}
      onClick={checkable ? onCheck : undefined}
    >
      {checkable && (
        <div className={Radio}>
          <RadioButton checked={checked} />
        </div>
      )}
      <div className={ColorBar} style={{ backgroundColor: barColor }} />
      <div className={TextAreaContainer}>
        <div className={TextContainer}>
          {header && <h3>{fm(`onlineRehabilitation.controlSteps.${cognition ? 'cognition.' : ''}${header}`)}</h3>}
          <div style={{ display: 'flex' }}>
            {editing ? (
              <div
                style={{
                  flex: 5,
                }}
              >
                <TextFieldArea
                  editing
                  input={{ name: 'editingControlStep', value: contentText, onChange: onEdit }}
                  rows={3}
                  viewStyle={{ width: '100%', marginTop: '0rem' }}
                />
              </div>
            ) : (
              <div
                style={{
                  whiteSpace: 'pre-line',
                  flex: 5,
                }}
              >
                {contentText}
              </div>
            )}
            {editable && (
              <div className={EditTextArea}>
                {!editing && (
                  <div className={EditText} onClick={setEditing}>
                    {fm('onlineRehabilitation.edit')}
                  </div>
                )}
                {editing && (
                  <div className={EditText} onClick={unsetEditing}>
                    {fm('onlineRehabilitation.endEditing')}
                  </div>
                )}
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

ControlStep.propTypes = {
  color: string,
  header: string,
  contentText: string,
  checked: bool,
  onCheck: func,
  editable: bool,
  onEdit: func,
  editing: bool,
  setEditing: func,
  unsetEditing: func,
  cognition: bool,
  fm: func,
};

const ControlSteps = ({ steps, checkedValue, onCheck, editable, onEdit, cognition, fm }) => {
  const currentStatus = steps['currentStatus'] || null;
  const nearFutureTarget = steps['nearFutureTarget'] || null;
  const betterStatus = steps['betterStatus'] || null;
  const clearlyBetterStatus = steps['clearlyBetterStatus'] || null;
  const clearlyWorseStatus = steps['clearlyWorseStatus'] || null;

  const onCheckItem = name => {
    onCheck(name);
  };
  const onEditItem = name => event => {
    onEdit(name)(event);
  };

  const [editing, setEditing] = React.useState(null);

  const setEditingItem = name => setEditing(name);
  const unsetEditingItem = () => setEditing(null);

  return (
    <div className={StepsWrapper}>
      {clearlyBetterStatus && (
        <ControlStep
          color="green"
          header="clearlyBetterStatus"
          contentText={clearlyBetterStatus}
          checked={checkedValue ? checkedValue === 'clearlyBetterStatus' : null}
          onCheck={() => onCheckItem('clearlyBetterStatus')}
          editable={editable && (!editing || editing === 'clearlyBetterStatus')}
          onEdit={onEditItem('clearlyBetterStatus')}
          editing={editing === 'clearlyBetterStatus'}
          setEditing={() => setEditingItem('clearlyBetterStatus')}
          unsetEditing={unsetEditingItem}
          cognition={cognition}
          fm={fm}
        />
      )}

      {betterStatus && (
        <ControlStep
          color="lightYellow"
          header="betterStatus"
          contentText={betterStatus}
          checked={checkedValue ? checkedValue === 'betterStatus' : null}
          onCheck={() => onCheckItem('betterStatus')}
          editable={editable && (!editing || editing === 'betterStatus')}
          onEdit={onEditItem('betterStatus')}
          editing={editing === 'betterStatus'}
          setEditing={() => setEditingItem('betterStatus')}
          unsetEditing={unsetEditingItem}
          cognition={cognition}
          fm={fm}
        />
      )}

      {nearFutureTarget && (
        <ControlStep
          color="yellow"
          header="nearFutureTarget"
          contentText={nearFutureTarget}
          checked={checkedValue ? checkedValue === 'nearFutureTarget' : null}
          onCheck={() => onCheckItem('nearFutureTarget')}
          editable={editable && (!editing || editing === 'nearFutureTarget')}
          onEdit={onEditItem('nearFutureTarget')}
          editing={editing === 'nearFutureTarget'}
          setEditing={() => setEditingItem('nearFutureTarget')}
          unsetEditing={unsetEditingItem}
          cognition={cognition}
          fm={fm}
        />
      )}

      {currentStatus && (
        <ControlStep
          color="darkYellow"
          header="currentStatus"
          contentText={currentStatus}
          checked={checkedValue ? checkedValue === 'currentStatus' : null}
          onCheck={() => onCheckItem('currentStatus')}
          editable={editable && (!editing || editing === 'currentStatus')}
          onEdit={onEditItem('currentStatus')}
          editing={editing === 'currentStatus'}
          setEditing={() => setEditingItem('currentStatus')}
          unsetEditing={unsetEditingItem}
          cognition={cognition}
          fm={fm}
        />
      )}

      {clearlyWorseStatus && (
        <ControlStep
          color="red"
          header="clearlyWorseStatus"
          contentText={clearlyWorseStatus}
          checked={checkedValue ? checkedValue === 'clearlyWorseStatus' : null}
          onCheck={() => onCheckItem('clearlyWorseStatus')}
          editable={editable && (!editing || editing === 'clearlyWorseStatus')}
          onEdit={onEditItem('clearlyWorseStatus')}
          editing={editing === 'clearlyWorseStatus'}
          setEditing={() => setEditingItem('clearlyWorseStatus')}
          unsetEditing={unsetEditingItem}
          cognition={cognition}
          fm={fm}
        />
      )}
    </div>
  );
};

ControlSteps.propTypes = {
  steps: object,
  checkedValue: string,
  onCheck: func,
  editable: bool,
  onEdit: func,
  cognition: bool,
  fm: func,
};

export default ControlSteps;
